import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete,
  Tooltip,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Settings,
  Close,
  Visibility,
  Refresh,
  Delete,
} from '@mui/icons-material';
import CalendarService from '../../services/calendar.service';
import ClientService from '../../services/client.service';
import AppointmentDialog from './AppointmentDialog';

/** Day cell colors — shared by calendar grid and legend swatches. */
const CALENDAR_DAY_COLORS = {
  working: { bg: '#e8f5e9', border: '#81c784' },
  off: { bg: '#ffe8e8', border: '#ef9a9a' },
  partial: { bg: '#fff4cc', border: '#ffcc80' },
  full: { bg: '#ffcdd2', border: '#e57373' },
};

const legendSwatchSx = (colors) => ({
  width: 16,
  height: 16,
  flexShrink: 0,
  bgcolor: colors.bg,
  border: '1px solid',
  borderColor: colors.border,
  borderRadius: 0.5,
});

/** Local calendar date as YYYY-MM-DD (avoid UTC shift from toISOString). */
const formatLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatAppointmentTime = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const m = value.match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : value.slice(0, 5);
  }
  return String(value).slice(0, 5);
};

const clientOptionLabel = (client) => {
  if (!client) return '';
  const name = String(client.name || '').trim();
  const phone = String(client.phone || '').trim();
  const id = client.id != null ? String(client.id) : '';
  if (phone) return `${name} · ${phone}`;
  if (id) return `${name} (#${id})`;
  return name;
};

const filterClientOptions = (options, inputValue, limit = 80) => {
  const q = String(inputValue || '').trim().toLowerCase();
  if (!q) return options.slice(0, limit);
  return options
    .filter((c) => {
      const name = String(c.name || '').toLowerCase();
      const phone = String(c.phone || '').toLowerCase();
      const id = String(c.id || '');
      return name.includes(q) || phone.includes(q) || id.includes(q);
    })
    .slice(0, limit);
};

const normalizePhoneDigits = (value) => String(value || '').replace(/\D/g, '');

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const nameTokens = (value) => normalizeName(value).split(' ').filter(Boolean);

const scoreClientMatch = (appointment, client) => {
  const queryName = appointment.booker_name || appointment.client?.name || '';
  const queryPhone = appointment.booker_phone || appointment.client?.phone || '';

  let score = 0;
  const qPhone = normalizePhoneDigits(queryPhone);
  const cPhone = normalizePhoneDigits(client.phone);

  if (qPhone.length >= 6 && cPhone.length >= 6) {
    if (qPhone === cPhone) score += 100;
    else if (cPhone.endsWith(qPhone) || qPhone.endsWith(cPhone)) score += 85;
    else if (qPhone.slice(-8) === cPhone.slice(-8)) score += 75;
  }

  const qName = normalizeName(queryName);
  const cName = normalizeName(client.name);
  if (qName && cName) {
    if (qName === cName) score += 90;
    else if (cName.includes(qName) || qName.includes(cName)) score += 65;
    else {
      const qt = nameTokens(qName);
      const ct = nameTokens(cName);
      if (qt.length > 0) {
        const matched = qt.filter((token) => {
          if (token.length < 2) return ct.some((c) => c.startsWith(token));
          return ct.some((c) => c === token || c.startsWith(token) || token.startsWith(c));
        });
        if (matched.length === qt.length) {
          score += 45 + matched.length * 12;
        } else if (matched.length > 0) {
          score += 20 + matched.length * 8;
        }
      }
    }
  }

  return score;
};

/** Up to 3 clients that best match this public booking's name or phone. */
const suggestClientsForAppointment = (appointment, clientsList, limit = 3) => {
  const queryName = appointment.booker_name || appointment.client?.name || '';
  const queryPhone = appointment.booker_phone || appointment.client?.phone || '';
  if (!queryName && !queryPhone) return [];

  const minScore = 28;
  const seen = new Set();

  return clientsList
    .map((client) => ({ client, score: scoreClientMatch(appointment, client) }))
    .filter(({ score, client }) => {
      if (score < minScore || seen.has(client.id)) return false;
      seen.add(client.id);
      return true;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ client }) => client);
};

const clientSuggestionChipLabel = (client) => {
  const name = String(client.name || '').trim() || `#${client.id}`;
  const phone = String(client.phone || '').trim();
  return phone ? `${name} · ${phone}` : name;
};

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthSchedule, setMonthSchedule] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [daySchedule, setDaySchedule] = useState(null);
  const [clients, setClients] = useState([]);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [weekSchedule, setWeekSchedule] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [arrivedMap, setArrivedMap] = useState({});
  const [confirmMessageByAppointment, setConfirmMessageByAppointment] = useState({});
  const [confirmingAppointmentId, setConfirmingAppointmentId] = useState(null);
  const [bulkSendingWhatsapp, setBulkSendingWhatsapp] = useState(false);
  const [confirmationStatusMap, setConfirmationStatusMap] = useState({});
  const [confirmFeedback, setConfirmFeedback] = useState('');
  const [linkPatientSelect, setLinkPatientSelect] = useState({});
  const [linkingAppointmentId, setLinkingAppointmentId] = useState(null);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancellingAppointment, setCancellingAppointment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const location = useLocation();
  const { user, hasPermission } = useAuth();
  const canCreateAppointment = hasPermission('calendar.create');
  const canConfigureCalendar = hasPermission('calendar.edit') || hasPermission('calendar.create');

  const quickClientIdParam = new URLSearchParams(location.search).get('clientId');
  const initialClientId = quickClientIdParam ? Number(quickClientIdParam) : null;
  const { t, language } = useTranslation();
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  // Appointment form state
  const [appointmentForm, setAppointmentForm] = useState({
    client_id: '',
    date: '',
    start_time: '',
    end_time: '',
    visit_id: ''
  });

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    weekday: 0,
    start_time: '09:00',
    end_time: '17:00',
    is_working_day: true,
    breaks: []
  });

  const [dateOverrides, setDateOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    is_working_day: true,
  });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideDeletingDate, setOverrideDeletingDate] = useState(null);
  const [overrideFeedback, setOverrideFeedback] = useState('');
  const [overrideError, setOverrideError] = useState('');

  const [publicCalendarLanguage, setPublicCalendarLanguage] = useState('en');
  const [publicLanguageSaving, setPublicLanguageSaving] = useState(false);
  const [publicLanguageFeedback, setPublicLanguageFeedback] = useState('');
  const [publicLanguageError, setPublicLanguageError] = useState('');

  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleFeedback, setScheduleFeedback] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSnackOpen, setScheduleSnackOpen] = useState(false);
  const [lastSavedWeekday, setLastSavedWeekday] = useState(null);

  useEffect(() => {
    loadMonthSchedule();
    loadClients();
    loadWeekSchedule();
  }, [currentDate]);

  useEffect(() => {
    if (settingsModalOpen && activeTab === 1) {
      loadDateOverrides();
    }
  }, [settingsModalOpen, activeTab]);

  useEffect(() => {
    if (settingsModalOpen) {
      loadCalendarConfig();
    }
  }, [settingsModalOpen]);

  useEffect(() => {
    try {
      const persisted = localStorage.getItem('calendar.arrivedQueue');
      if (persisted) {
        setArrivedMap(JSON.parse(persisted));
      }
    } catch (error) {
      console.error('Error reading queue state:', error);
    }
  }, []);

  useEffect(() => {
    // Client quick-action: open the appointment dialog prefilled with the client.
    if (!initialClientId || Number.isNaN(initialClientId)) return;
    const todayStr = formatLocalDateKey(new Date());
    setSelectedSlot(null);
    setSelectedDate(todayStr);
    setAppointmentDialogOpen(true);
  }, [initialClientId]);

  const loadMonthSchedule = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const response = await CalendarService.getMonthSchedule(year, month);
      setMonthSchedule(response.data);
    } catch (error) {
      console.error('Error loading month schedule:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await ClientService.getClients();
      setClients(response.data?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadWeekSchedule = async () => {
    try {
      const response = await CalendarService.getWeekSchedule();
      setWeekSchedule(response.data);
    } catch (error) {
      console.error('Error loading week schedule:', error);
    }
  };

  const loadCalendarConfig = async () => {
    try {
      const response = await CalendarService.getCalendarConfig();
      const lang = response.data?.public_calendar_language;
      setPublicCalendarLanguage(lang === 'es' ? 'es' : 'en');
    } catch (error) {
      console.error('Error loading calendar config:', error);
    }
  };

  const handleSavePublicLanguage = async () => {
    try {
      setPublicLanguageSaving(true);
      setPublicLanguageError('');
      setPublicLanguageFeedback('');
      await CalendarService.saveCalendarConfig({
        public_calendar_language: publicCalendarLanguage,
      });
      setPublicLanguageFeedback(t('calendar.settings.publicLanguageSaved'));
    } catch (error) {
      console.error('Error saving public calendar language:', error);
      setPublicLanguageError(
        error?.response?.data?.message || t('calendar.settings.publicLanguageError')
      );
    } finally {
      setPublicLanguageSaving(false);
    }
  };

  const loadDateOverrides = async () => {
    try {
      const response = await CalendarService.getOverrides();
      setDateOverrides(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading date overrides:', error);
    }
  };

  const applyOverrideToForm = (override) => {
    setOverrideForm({
      date: override.date,
      start_time: toTimeInputValue(override.start_time, '09:00'),
      end_time: toTimeInputValue(override.end_time, '17:00'),
      is_working_day: Boolean(override.is_working_day),
    });
  };

  const handleOverrideDateChange = (dateStr) => {
    if (!dateStr) {
      setOverrideForm((prev) => ({ ...prev, date: '' }));
      return;
    }
    const existing = dateOverrides.find((o) => o.date === dateStr);
    if (existing) {
      applyOverrideToForm(existing);
      return;
    }
    const weekday = new Date(`${dateStr}T12:00:00`).getDay();
    const cfg = getWeekdayConfig(weekday);
    setOverrideForm({
      date: dateStr,
      start_time: cfg?.start_time || '09:00',
      end_time: cfg?.end_time || '17:00',
      is_working_day: cfg?.is_working_day ?? true,
    });
  };

  const getDefaultWeekdayHint = () => {
    if (!overrideForm.date) return null;
    const weekday = new Date(`${overrideForm.date}T12:00:00`).getDay();
    const cfg = getWeekdayConfig(weekday);
    const dayName = t(
      `calendar.settings.weekdays.${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][weekday]}`
    );
    const isWorking = cfg ? cfg.is_working_day : true;
    const status = isWorking ? t('calendar.legend.workingDay') : t('calendar.legend.offDay');
    const range = cfg ? `${cfg.start_time} - ${cfg.end_time}` : '09:00 - 17:00';
    return t('calendar.settings.defaultForWeekday', { status: `${dayName}: ${status}`, range });
  };

  const handleOverrideSubmit = async () => {
    if (!overrideForm.date) {
      setOverrideError(t('calendar.settings.overrideDateRequired'));
      return;
    }
    try {
      setOverrideSaving(true);
      setOverrideError('');
      setOverrideFeedback('');
      await CalendarService.overrideSpecificDate({
        date: overrideForm.date,
        start_time: overrideForm.start_time,
        end_time: overrideForm.end_time,
        is_working_day: overrideForm.is_working_day,
        breaks: [],
      });
      await loadDateOverrides();
      await loadMonthSchedule();
      setOverrideFeedback(t('calendar.settings.overrideSaved'));
    } catch (error) {
      console.error('Error saving date override:', error);
      setOverrideError(
        error?.response?.data?.message || t('calendar.settings.overrideError')
      );
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleOverrideDelete = async (dateStr) => {
    try {
      setOverrideDeletingDate(dateStr);
      setOverrideError('');
      setOverrideFeedback('');
      await CalendarService.deleteOverride(dateStr);
      if (overrideForm.date === dateStr) {
        setOverrideForm({
          date: '',
          start_time: '09:00',
          end_time: '17:00',
          is_working_day: true,
        });
      }
      await loadDateOverrides();
      await loadMonthSchedule();
      setOverrideFeedback(t('calendar.settings.overrideRemoved'));
    } catch (error) {
      console.error('Error deleting date override:', error);
      setOverrideError(
        error?.response?.data?.message || t('calendar.settings.overrideDeleteError')
      );
    } finally {
      setOverrideDeletingDate(null);
    }
  };

  const formatOverrideListDate = (dateStr) => {
    try {
      return new Date(`${dateStr}T12:00:00`).toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const toTimeInputValue = (timeValue, fallback = '09:00') => {
    if (!timeValue) return fallback;
    // Backend can return "HH:mm:ss", "HH:mm", or full datetime strings.
    if (typeof timeValue === 'string') {
      const timeMatch = timeValue.match(/(\d{2}):(\d{2})/);
      if (timeMatch) return `${timeMatch[1]}:${timeMatch[2]}`;
    }
    return fallback;
  };

  const normalizeBreaks = (breaks) => {
    if (Array.isArray(breaks)) return breaks;
    if (typeof breaks === 'string' && breaks.trim()) {
      try {
        const parsed = JSON.parse(breaks);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getWeekdayConfig = (weekday) => {
    const setting = weekSchedule?.[weekday];
    if (!setting) return null;
    return {
      weekday,
      start_time: toTimeInputValue(setting.start_time, '09:00'),
      end_time: toTimeInputValue(setting.end_time, '17:00'),
      is_working_day: Boolean(setting.is_working_day),
      breaks: normalizeBreaks(setting.breaks),
    };
  };

  // Load saved config when user picks a different weekday in settings.
  useEffect(() => {
    if (!settingsModalOpen || activeTab !== 0) return;
    const config = getWeekdayConfig(scheduleForm.weekday);
    if (!config) return;
    setScheduleForm((prev) => ({
      ...prev,
      weekday: scheduleForm.weekday,
      start_time: config.start_time,
      end_time: config.end_time,
      is_working_day: config.is_working_day,
      breaks: config.breaks || [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekSchedule, scheduleForm.weekday, settingsModalOpen, activeTab]);

  const buildWeekdaySchedulePayload = () => {
    const start = toTimeInputValue(scheduleForm.start_time, '09:00');
    const end = toTimeInputValue(scheduleForm.end_time, '17:00');
    return {
      weekday: Number(scheduleForm.weekday),
      start_time: start,
      end_time: end,
      is_working_day:
        scheduleForm.is_working_day === true ||
        scheduleForm.is_working_day === 'true' ||
        scheduleForm.is_working_day === 1 ||
        scheduleForm.is_working_day === '1',
      breaks: normalizeBreaks(scheduleForm.breaks),
    };
  };

  const openDayModal = async (date) => {
    try {
      const response = await CalendarService.getDaySchedule(date);
      setDaySchedule(response.data);
      setSelectedDate(date);
      if (response.data?.appointments?.length) {
        const arrivedFromApi = {};
        response.data.appointments.forEach((appointment) => {
          if (appointment.arrived_at) {
            arrivedFromApi[appointment.id] = true;
          }
        });
        setArrivedMap((prev) => ({ ...prev, ...arrivedFromApi }));
      }
      setDayModalOpen(true);
    } catch (error) {
      console.error('Error loading day schedule:', error);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadMonthSchedule();
      if (dayModalOpen && selectedDate) {
        await openDayModal(selectedDate);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const closeDayModal = () => {
    setDayModalOpen(false);
    setSelectedDate(null);
    setDaySchedule(null);
    setExpandedSlot(null);
    setConfirmFeedback('');
  };

  const toggleSlotExpansion = (slotStart) => {
    setExpandedSlot(expandedSlot === slotStart ? null : slotStart);
  };

  const handleSlotClientSelect = async (slot, clientId) => {
    try {
      setIsCreatingAppointment(true);
      const client = clients.find((c) => String(c.id) === String(clientId));

      if (slot && client) {
        const appointmentData = {
          client_id: clientId,
          date: selectedDate,
          start_time: slot.start_time,
          end_time: slot.end_time,
          visit_id: null,
        };
        await CalendarService.createAppointment(appointmentData);
        await loadMonthSchedule();
        await openDayModal(selectedDate);
        setExpandedSlot(null);
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
    } finally {
      setIsCreatingAppointment(false);
    }
  };

  const isPublicBooking = (appointment) => appointment?.booking_source === 'public';

  const appointmentDisplayName = (appointment) =>
    appointment?.client?.name ||
    appointment?.booker_name ||
    t('calendar.appointmentLabels.unknownClient');

  const appointmentDisplayPhone = (appointment) =>
    appointment?.client?.phone || appointment?.booker_phone || null;

  const resolveLinkPatientClient = (appointment) => {
    const raw = linkPatientSelect[appointment.id];
    if (raw !== undefined && raw !== '') {
      const id = Number(raw);
      return clients.find((c) => Number(c.id) === id) || null;
    }
    if (appointment.client_id) {
      return clients.find((c) => Number(c.id) === Number(appointment.client_id)) || null;
    }
    return null;
  };

  const handleLinkPublicToClient = async (appointment) => {
    const raw =
      linkPatientSelect[appointment.id] ??
      appointment.client_id ??
      '';
    const clientId = Number(raw);
    if (!clientId || Number.isNaN(clientId)) {
      setConfirmFeedback(t('calendar.linkPublic.selectPatientFirst'));
      return;
    }
    try {
      setLinkingAppointmentId(appointment.id);
      setConfirmFeedback('');
      await CalendarService.updateAppointment(appointment.id, { client_id: clientId });
      setLinkPatientSelect((prev) => {
        const next = { ...prev };
        delete next[appointment.id];
        return next;
      });
      await openDayModal(selectedDate);
      setConfirmFeedback(t('calendar.linkPublic.success'));
    } catch (error) {
      console.error('Error linking appointment to client:', error);
      setConfirmFeedback(t('calendar.linkPublic.error'));
    } finally {
      setLinkingAppointmentId(null);
    }
  };

  const handleSaveAsNewClientFromBooking = async (appointment) => {
    const name = String(appointment.booker_name || appointment.client?.name || '').trim();
    if (!name) {
      setConfirmFeedback(t('calendar.linkPublic.nameRequired'));
      return;
    }
    try {
      setLinkingAppointmentId(appointment.id);
      setConfirmFeedback('');
      const phone = appointment.booker_phone || appointment.client?.phone || '';
      const res = await ClientService.createClient({
        name,
        ...(phone ? { phone } : {}),
      });
      const created = res.data?.data ?? res.data;
      const clientId = created?.id;
      if (!clientId) {
        throw new Error('No client id returned');
      }
      await CalendarService.updateAppointment(appointment.id, { client_id: clientId });
      setLinkPatientSelect((prev) => {
        const next = { ...prev };
        delete next[appointment.id];
        return next;
      });
      await loadMonthSchedule();
      await openDayModal(selectedDate);
      setConfirmFeedback(t('calendar.linkPublic.savedAsNewClient'));
    } catch (error) {
      console.error('Error saving booking as new client:', error);
      setConfirmFeedback(t('calendar.linkPublic.saveAsNewClientError'));
    } finally {
      setLinkingAppointmentId(null);
    }
  };

  const requestAppointmentCancel = (appointment) => {
    setAppointmentToCancel(appointment);
  };

  const confirmAppointmentCancel = async () => {
    if (!appointmentToCancel) return;
    try {
      setCancellingAppointment(true);
      await CalendarService.deleteAppointment(appointmentToCancel.id);
      setAppointmentToCancel(null);
      await loadMonthSchedule();
      await openDayModal(selectedDate);
      setConfirmFeedback(t('calendar.form.cancelAppointmentSuccess'));
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setConfirmFeedback(t('calendar.form.cancelAppointmentError'));
    } finally {
      setCancellingAppointment(false);
    }
  };

  const persistArrivedMap = (nextMap) => {
    setArrivedMap(nextMap);
    try {
      localStorage.setItem('calendar.arrivedQueue', JSON.stringify(nextMap));
    } catch (error) {
      console.error('Error saving queue state:', error);
    }
  };

  const handleQueueCheck = async (appointmentId, checked) => {
    const nextMap = { ...arrivedMap, [appointmentId]: checked };
    persistArrivedMap(nextMap);
    try {
      await CalendarService.markAppointmentArrived(appointmentId, checked);
    } catch (error) {
      console.error('Error saving arrival status:', error);
      const rollback = { ...arrivedMap };
      if (checked) {
        delete rollback[appointmentId];
      } else {
        rollback[appointmentId] = true;
      }
      persistArrivedMap(rollback);
    }
  };

  const buildDefaultConfirmationMessage = (appointment) => {
    const patientName = appointmentDisplayName(appointment);
    return t('calendar.confirmation.defaultMessage', {
      patient: patientName,
      date: selectedDate || '',
      start: appointment.start_time || '',
      end: appointment.end_time || ''
    });
  };

  const getConfirmationMessage = (appointment) => {
    return confirmMessageByAppointment[appointment.id] || buildDefaultConfirmationMessage(appointment);
  };

  const setConfirmationMessage = (appointmentId, value) => {
    setConfirmMessageByAppointment((prev) => ({ ...prev, [appointmentId]: value }));
  };

  const normalizePhone = (phone = '') => String(phone).replace(/[^\d]/g, '');

  const handleAppointmentConfirm = async (appointment, method = 'only') => {
    try {
      setConfirmingAppointmentId(appointment.id);
      setConfirmFeedback('');
      await CalendarService.confirmAppointment(appointment.id);
      const message = getConfirmationMessage(appointment);

      if (method === 'whatsapp') {
        const phone = normalizePhone(appointmentDisplayPhone(appointment));
        if (!phone) {
          setConfirmFeedback(t('calendar.confirmation.phoneRequiredForWhatsapp'));
          setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'whatsapp_failed' }));
        } else {
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
          setConfirmFeedback(t('calendar.confirmation.sentToWhatsapp'));
          setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'whatsapp_sent' }));
        }
      } else if (method === 'email') {
        const email = appointment.client?.email;
        if (!email) {
          setConfirmFeedback(t('calendar.confirmation.emailRequired'));
          setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'email_failed' }));
        } else {
          const subject = encodeURIComponent(t('calendar.confirmation.emailSubject'));
          window.open(`mailto:${email}?subject=${subject}&body=${encodeURIComponent(message)}`, '_self');
          setConfirmFeedback(t('calendar.confirmation.sentToEmail'));
          setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'email_sent' }));
        }
      } else {
        setConfirmFeedback(t('calendar.confirmation.confirmedOnly'));
        setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'confirmed_only' }));
      }

      await openDayModal(selectedDate);
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setConfirmFeedback(t('calendar.confirmation.errorConfirming'));
      setConfirmationStatusMap((prev) => ({ ...prev, [appointment.id]: 'confirm_failed' }));
    } finally {
      setConfirmingAppointmentId(null);
    }
  };

  const handleBulkWhatsapp = async () => {
    try {
      if (!daySchedule?.appointments?.length) return;
      setBulkSendingWhatsapp(true);
      setConfirmFeedback('');
      let sentCount = 0;
      let failedCount = 0;
      const nextStatuses = {};

      daySchedule.appointments.forEach((appointment) => {
        const message = getConfirmationMessage(appointment);
        const phone = normalizePhone(appointmentDisplayPhone(appointment));
        if (!phone) {
          failedCount += 1;
          nextStatuses[appointment.id] = 'whatsapp_failed';
          return;
        }

        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
        sentCount += 1;
        nextStatuses[appointment.id] = 'whatsapp_sent';
      });

      setConfirmationStatusMap((prev) => ({ ...prev, ...nextStatuses }));
      setConfirmFeedback(t('calendar.confirmation.bulkWhatsappResult', { success: sentCount, failed: failedCount }));
    } catch (error) {
      console.error('Error bulk WhatsApp send:', error);
      setConfirmFeedback(t('calendar.confirmation.errorBulkWhatsapp'));
    } finally {
      setBulkSendingWhatsapp(false);
    }
  };

  const getConfirmationStatusMeta = (appointment) => {
    if (!appointment) return null;

    const map = {
      confirmed_only: { label: t('calendar.confirmation.status.confirmedOnly'), color: 'success' },
      whatsapp_sent: { label: t('calendar.confirmation.status.whatsappSent'), color: 'info' },
      email_sent: { label: t('calendar.confirmation.status.emailSent'), color: 'info' },
      whatsapp_failed: { label: t('calendar.confirmation.status.whatsappFailed'), color: 'error' },
      email_failed: { label: t('calendar.confirmation.status.emailFailed'), color: 'error' },
      confirm_failed: { label: t('calendar.confirmation.status.confirmFailed'), color: 'error' },
    };

    const ephemeralStatus = confirmationStatusMap[appointment.id];
    if (ephemeralStatus) {
      return map[ephemeralStatus] || null;
    }

    if (appointment.status === 'confirmed') {
      return map.confirmed_only;
    }

    return null;
  };

  const handleMonthChange = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'next') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
      return newDate;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    
    // Add previous month's days
    for (let i = startingDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({
        date: prevDate,
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      days.push({
        date: currentDate,
        isCurrentMonth: true,
        isToday: currentDate.toDateString() === new Date().toDateString()
      });
    }

    // Add next month's days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({
        date: nextDate,
        isCurrentMonth: false,
        isToday: false
      });
    }

    return days;
  };

  const getDayStatus = (date) => {
    const dateStr = formatLocalDateKey(date);
    const schedule = monthSchedule[dateStr];
    
    if (!schedule) return { isWorkingDay: false, available: 0, booked: 0 };
    
    return {
      isWorkingDay: schedule.is_working_day,
      available: schedule.available_slots || 0,
      booked: schedule.booked_slots || 0
    };
  };

  const getDayBackgroundColor = (date) => {
    const status = getDayStatus(date);
    
    if (!status.isWorkingDay) return CALENDAR_DAY_COLORS.off.bg;
    if (status.booked > 0 && status.available === 0) return CALENDAR_DAY_COLORS.full.bg;
    if (status.booked > 0) return CALENDAR_DAY_COLORS.partial.bg;
    return CALENDAR_DAY_COLORS.working.bg;
  };

  const getDayBorderColor = (date) => {
    const status = getDayStatus(date);

    if (!status.isWorkingDay) return CALENDAR_DAY_COLORS.off.border;
    if (status.booked > 0 && status.available === 0) return CALENDAR_DAY_COLORS.full.border;
    if (status.booked > 0) return CALENDAR_DAY_COLORS.partial.border;
    return CALENDAR_DAY_COLORS.working.border;
  };

  const handleAppointmentSubmit = async () => {
    try {
      await CalendarService.createAppointment(appointmentForm);
      closeDayModal();
      loadMonthSchedule();
      setAppointmentForm({
        client_id: '',
        date: '',
        start_time: '',
        end_time: '',
        visit_id: ''
      });
    } catch (error) {
      console.error('Error creating appointment:', error);
    }
  };

  const showScheduleMessage = (message, severity = 'success') => {
    if (severity === 'success') {
      setScheduleFeedback(message);
      setScheduleError('');
      setScheduleSnackOpen(true);
    } else {
      setScheduleError(message);
      setScheduleFeedback('');
      setScheduleSnackOpen(true);
    }
  };

  const handleScheduleSubmit = async (event) => {
    event?.preventDefault?.();
    const payload = buildWeekdaySchedulePayload();
    const startMins =
      parseInt(payload.start_time.slice(0, 2), 10) * 60 +
      parseInt(payload.start_time.slice(3, 5), 10);
    const endMins =
      parseInt(payload.end_time.slice(0, 2), 10) * 60 +
      parseInt(payload.end_time.slice(3, 5), 10);
    if (endMins <= startMins) {
      showScheduleMessage(t('calendar.settings.scheduleEndBeforeStart'), 'error');
      return;
    }

    try {
      setScheduleSaving(true);
      setScheduleError('');
      setScheduleFeedback('');
      const response = await CalendarService.saveWeekdaySchedule(payload);
      const saved = response.data?.data;
      if (saved && typeof saved.weekday === 'number') {
        setWeekSchedule((prev) => ({
          ...(prev || {}),
          [saved.weekday]: saved,
        }));
        setLastSavedWeekday(saved.weekday);
        setScheduleForm((prev) => ({
          ...prev,
          start_time: toTimeInputValue(saved.start_time, prev.start_time),
          end_time: toTimeInputValue(saved.end_time, prev.end_time),
          is_working_day: Boolean(saved.is_working_day),
        }));
      } else {
        await loadWeekSchedule();
      }
      await loadMonthSchedule();
      showScheduleMessage(t('calendar.settings.scheduleSaved'), 'success');
    } catch (error) {
      console.error('Error saving schedule:', error);
      const apiMessage = error?.response?.data?.message;
      const validationMsg = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : null;
      showScheduleMessage(
        validationMsg || apiMessage || t('calendar.settings.scheduleError'),
        'error'
      );
    } finally {
      setScheduleSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const days = getDaysInMonth(currentDate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
      <Paper sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'grey.200', px: 4, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" sx={{ color: 'grey.800', fontWeight: 'bold' }}>
              {t('calendar.pageTitle')}
            </Typography>
            <Typography sx={{ color: 'grey.600', mt: 1 }}>
              {t('calendar.pageSubtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {canCreateAppointment && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
            >
              {t('calendar.newAppointment')}
            </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => {
                const tid = user?.tenant_id;
                const qs = tid ? `?tenantId=${encodeURIComponent(tid)}` : '';
                window.open(`/calendar/public${qs}`, '_blank', 'noopener,noreferrer');
              }}
              sx={{ borderColor: 'grey.300', color: 'grey.600' }}
            >
              {t('calendar.public.openPublicView')}
            </Button>
            {canConfigureCalendar && (
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => {
                setScheduleFeedback('');
                setScheduleError('');
                setScheduleSnackOpen(false);
                setLastSavedWeekday(null);
                setSettingsModalOpen(true);
                loadWeekSchedule();
              }}
              sx={{ borderColor: 'grey.300', color: 'grey.600' }}
            >
              {t('calendar.configure')}
            </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Calendar Section */}
      <Box sx={{ flex: 1, p: 4 }}>
          {/* Month Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => handleMonthChange('prev')}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h5" sx={{ color: 'grey.800', fontWeight: 'semibold' }}>
                {currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
              </Typography>
              <IconButton onClick={() => handleMonthChange('next')}>
                <ChevronRight />
              </IconButton>
              <Tooltip title={t('calendar.refreshTooltip')}>
                <span>
                  <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    aria-label={t('common.refresh')}
                    sx={{ color: 'grey.600' }}
                  >
                    {refreshing ? <CircularProgress size={22} /> : <Refresh />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            
            {/* Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={legendSwatchSx(CALENDAR_DAY_COLORS.working)} />
                <Typography variant="body2" sx={{ color: 'grey.600' }}>{t('calendar.legend.workingDay')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={legendSwatchSx(CALENDAR_DAY_COLORS.off)} />
                <Typography variant="body2" sx={{ color: 'grey.600' }}>{t('calendar.legend.offDay')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={legendSwatchSx(CALENDAR_DAY_COLORS.partial)} />
                <Typography variant="body2" sx={{ color: 'grey.600' }}>{t('calendar.legend.partiallyBooked')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={legendSwatchSx(CALENDAR_DAY_COLORS.full)} />
                <Typography variant="body2" sx={{ color: 'grey.600' }}>{t('calendar.legend.fullyBooked')}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Calendar Grid */}
          <Paper sx={{ bgcolor: 'white', borderRadius: 2, shadow: 1, border: '1px solid', borderColor: 'grey.200' }}>
            {/* Days of Week Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid', borderColor: 'grey.200' }}>
              {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((dayKey) => (
                <Box
                  key={dayKey}
                  sx={{ p: 2, textAlign: 'center', fontWeight: 'semibold', color: 'grey.600', borderRight: '1px solid', borderColor: 'grey.200' }}
                >
                  {t(`calendar.days.${dayKey}`)}
                </Box>
              ))}
            </Box>

            {/* Calendar Days */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {days.map((day, index) => {
                const status = getDayStatus(day.date);
                const bgColor = getDayBackgroundColor(day.date);
                const borderColorByStatus = getDayBorderColor(day.date);
                const isOtherMonth = !day.isCurrentMonth;
                const otherMonthLabel = day.date.toLocaleDateString(locale, { month: 'long' });
                
                return (
                  <Box
                    key={index}
                    sx={{
                      height: 120,
                      borderRight: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: isOtherMonth ? 'grey.200' : borderColorByStatus,
                      p: 1,
                      cursor: isOtherMonth ? 'default' : 'pointer',
                      backgroundColor: isOtherMonth ? 'grey.50' : bgColor,
                      '&:hover': isOtherMonth ? undefined : { filter: 'brightness(0.98)' },
                      ...(day.isToday && { border: '2px solid', borderColor: 'blue.500' })
                    }}
                    onClick={() => {
                      if (isOtherMonth) return;
                      openDayModal(formatLocalDateKey(day.date));
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'semibold',
                        color: isOtherMonth ? 'grey.400' : 'grey.800',
                        mb: 1
                      }}
                    >
                      {day.date.getDate()}
                    </Typography>
                    
                    {isOtherMonth ? (
                      <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>
                        {otherMonthLabel}
                      </Typography>
                    ) : status.isWorkingDay ? (
                      <>
                        <Typography variant="caption" sx={{ color: 'green.600', display: 'block' }}>
                          {status.available} {t('calendar.availability.available')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>
                          {status.booked} {t('calendar.availability.booked')}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'red.600' }}>
                        {t('calendar.offDay')}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>

      {/* Day Modal */}
      <Dialog
        open={dayModalOpen}
        onClose={closeDayModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedDate ? formatDate(new Date(selectedDate)) : ''}
            </Typography>
            <IconButton onClick={closeDayModal} sx={{ color: 'grey.500' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {daySchedule && (
            <Box sx={{ mt: 2 }}>
              {!!confirmFeedback && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {confirmFeedback}
                </Alert>
              )}
              {!daySchedule.is_working_day ? (
                <Alert severity="info">{t('calendar.alerts.offDay')}</Alert>
              ) : (
                <Grid container spacing={3}>
                  {/* Available Time Slots */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" sx={{ mb: 2 }}>{t('calendar.availableTimeSlots')}</Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      maxHeight: 400,
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      p: 1
                    }}>
                      {daySchedule.time_slots?.filter((slot) => slot.is_available).map((slot) => (
                        <Box key={slot.start_time}>
                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{ 
                              borderColor: 'green.200', 
                              bgcolor: 'green.50',
                              color: 'green.800',
                              '&:hover': { bgcolor: 'green.100' },
                              mb: 1
                            }}
                            onClick={() => toggleSlotExpansion(slot.start_time)}
                          >
                            <Box sx={{ textAlign: 'left', width: '100%' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {slot.start_time} - {slot.end_time}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'green.600' }}>
                                {t('calendar.availability.available')}
                              </Typography>
                            </Box>
                          </Button>
                          
                          {/* Expanded Slot with Client Selection */}
                          {expandedSlot === slot.start_time && (
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'grey.100', 
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}>
                              <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
                                {t('calendar.selectClientForSlot', { start: slot.start_time, end: slot.end_time })}
                              </Typography>
                              <Autocomplete
                                fullWidth
                                size="small"
                                disabled={isCreatingAppointment}
                                options={clients}
                                getOptionLabel={clientOptionLabel}
                                filterOptions={(options, state) =>
                                  filterClientOptions(options, state.inputValue)
                                }
                                isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                                onChange={(_, client) => {
                                  if (client) handleSlotClientSelect(slot, client.id);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={t('calendar.client')}
                                    placeholder={t('calendar.searchClient')}
                                  />
                                )}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setExpandedSlot(null)}
                                sx={{ mr: 1 }}
                                disabled={isCreatingAppointment}
                              >
                                {isCreatingAppointment ? t('calendar.form.creating') : t('calendar.form.cancel')}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      ))}
                      
                      {daySchedule.time_slots?.filter(slot => slot.is_available).length === 0 && (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 2 }}>
                          {t('calendar.noAvailableTimeSlots')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Booked Appointments */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('calendar.bookedAppointments')} ({daySchedule.appointments?.length || 0})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {t('calendar.secretaryQueue')}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      maxHeight: 480,
                      overflowY: 'auto'
                    }}>
                      {daySchedule.appointments?.map((appointment) => (
                        <Box key={appointment.id}>
                          <Box
                            sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'red.200',
                              bgcolor: 'red.50',
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'red.100' }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'red.800' }}>
                              {formatAppointmentTime(appointment.start_time)} - {formatAppointmentTime(appointment.end_time)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'red.600' }}>
                              {appointmentDisplayName(appointment)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'grey.700', display: 'block' }}>
                              {t('calendar.fields.phone')}: {appointmentDisplayPhone(appointment) || t('calendar.common.na')}
                            </Typography>
                            {isPublicBooking(appointment) && (
                              <Chip
                                size="small"
                                label={t('calendar.linkPublic.badge')}
                                color="warning"
                                sx={{ mt: 0.5, mb: 0.5 }}
                              />
                            )}
                            {isPublicBooking(appointment) && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                                  {t('calendar.linkPublic.hint')}
                                </Typography>
                                {(() => {
                                  const suggestions = suggestClientsForAppointment(appointment, clients);
                                  const selected = resolveLinkPatientClient(appointment);
                                  if (!suggestions.length) return null;
                                  return (
                                    <Box sx={{ mb: 1 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 0.5 }}
                                      >
                                        {t('calendar.linkPublic.suggestedMatches')}
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {suggestions.map((client) => {
                                          const isSelected =
                                            selected && Number(selected.id) === Number(client.id);
                                          return (
                                            <Chip
                                              key={client.id}
                                              size="small"
                                              label={clientSuggestionChipLabel(client)}
                                              clickable
                                              color={isSelected ? 'primary' : 'default'}
                                              variant={isSelected ? 'filled' : 'outlined'}
                                              onClick={() =>
                                                setLinkPatientSelect((prev) => ({
                                                  ...prev,
                                                  [appointment.id]: String(client.id),
                                                }))
                                              }
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                })()}
                                <Autocomplete
                                  fullWidth
                                  size="small"
                                  sx={{ mb: 1 }}
                                  options={clients}
                                  value={resolveLinkPatientClient(appointment)}
                                  getOptionLabel={clientOptionLabel}
                                  filterOptions={(options, state) =>
                                    filterClientOptions(options, state.inputValue)
                                  }
                                  isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                                  onChange={(_, client) =>
                                    setLinkPatientSelect((prev) => ({
                                      ...prev,
                                      [appointment.id]: client ? String(client.id) : '',
                                    }))
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t('calendar.linkPublic.selectPatient')}
                                      placeholder={t('calendar.linkPublic.searchPatient')}
                                    />
                                  )}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  disabled={linkingAppointmentId === appointment.id}
                                  onClick={() => handleLinkPublicToClient(appointment)}
                                  sx={{ mb: 1 }}
                                >
                                  {linkingAppointmentId === appointment.id
                                    ? t('calendar.linkPublic.linking')
                                    : t('calendar.linkPublic.linkButton')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  fullWidth
                                  disabled={linkingAppointmentId === appointment.id}
                                  onClick={() => handleSaveAsNewClientFromBooking(appointment)}
                                >
                                  {linkingAppointmentId === appointment.id
                                    ? t('calendar.linkPublic.saving')
                                    : t('calendar.linkPublic.saveAsNewClientButton')}
                                </Button>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ color: 'grey.500' }}>
                              {appointment.visit?.title || t('calendar.appointmentLabels.appointment')}
                            </Typography>
                            <FormControlLabel
                              sx={{ mt: 0.5 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={Boolean(arrivedMap[appointment.id])}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    handleQueueCheck(appointment.id, event.target.checked);
                                  }}
                                />
                              }
                              label={t('calendar.queue.arrived')}
                            />
                          </Box>
                        </Box>
                      ))}
                      
                      {daySchedule.appointments?.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 2 }}>
                          {t('calendar.noAppointments')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Confirmation Column */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">{t('calendar.confirmation.columnTitle')}</Typography>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleBulkWhatsapp}
                        disabled={bulkSendingWhatsapp || !daySchedule.appointments?.length}
                      >
                        {bulkSendingWhatsapp ? t('calendar.confirmation.bulkSendingWhatsapp') : t('calendar.confirmation.bulkSendWhatsapp')}
                      </Button>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {t('calendar.confirmation.columnSubtitle')}
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      maxHeight: 480,
                      overflowY: 'auto'
                    }}>
                      {daySchedule.appointments?.map((appointment) => {
                        const confirmationMeta = getConfirmationStatusMeta(appointment);
                        return (
                        <Box
                          key={`confirm-${appointment.id}`}
                          sx={{
                            p: 2,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {formatAppointmentTime(appointment.start_time)} - {formatAppointmentTime(appointment.end_time)} • {appointmentDisplayName(appointment)}
                            </Typography>
                            {isPublicBooking(appointment) && (
                              <Chip size="small" label={t('calendar.linkPublic.badge')} color="warning" />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                            <strong>{t('calendar.fields.phone')}:</strong> {appointmentDisplayPhone(appointment) || t('calendar.common.na')}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                            <strong>{t('calendar.fields.email')}:</strong> {appointment.client?.email || t('calendar.common.na')}
                          </Typography>
                          {confirmationMeta && (
                            <Chip
                              size="small"
                              label={confirmationMeta.label}
                              color={confirmationMeta.color}
                              sx={{ mb: 1 }}
                            />
                          )}
                          <Divider sx={{ my: 1.5 }} />
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={getConfirmationMessage(appointment)}
                            onChange={(event) => setConfirmationMessage(appointment.id, event.target.value)}
                            label={t('calendar.confirmation.messageLabel')}
                            sx={{ mb: 1 }}
                          />
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              onClick={() => handleAppointmentConfirm(appointment, 'only')}
                              disabled={confirmingAppointmentId === appointment.id}
                            >
                              {t('calendar.confirmation.confirmOnly')}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => handleAppointmentConfirm(appointment, 'whatsapp')}
                              disabled={confirmingAppointmentId === appointment.id}
                            >
                              {t('calendar.confirmation.confirmWhatsapp')}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              onClick={() => handleAppointmentConfirm(appointment, 'email')}
                              disabled={confirmingAppointmentId === appointment.id}
                            >
                              {t('calendar.confirmation.confirmEmail')}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setConfirmationMessage(appointment.id, buildDefaultConfirmationMessage(appointment))}
                              disabled={confirmingAppointmentId === appointment.id}
                            >
                              {t('calendar.confirmation.useDefault')}
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => requestAppointmentCancel(appointment)}
                              disabled={confirmingAppointmentId === appointment.id}
                            >
                              {t('calendar.form.cancelAppointment')}
                            </Button>
                          </Box>
                        </Box>
                      );
                      })}

                      {daySchedule.appointments?.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 2 }}>
                          {t('calendar.noAppointments')}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">{t('calendar.settings.title')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label={t('calendar.settings.tabs.weeklySchedule')} />
            <Tab label={t('calendar.settings.tabs.customDates')} />
            <Tab label={t('calendar.settings.tabs.publicBooking')} />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('calendar.settings.defaultWeeklySchedule')}</Typography>
              {scheduleFeedback && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setScheduleFeedback('')}>
                  {scheduleFeedback}
                </Alert>
              )}
              {scheduleError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError('')}>
                  {scheduleError}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t('calendar.settings.weekday')}</InputLabel>
                    <Select
                      value={scheduleForm.weekday}
                      onChange={(e) => setScheduleForm(prev => ({ ...prev, weekday: Number(e.target.value) }))}
                    >
                      <MenuItem value={0}>{t('calendar.settings.weekdays.sunday')}</MenuItem>
                      <MenuItem value={1}>{t('calendar.settings.weekdays.monday')}</MenuItem>
                      <MenuItem value={2}>{t('calendar.settings.weekdays.tuesday')}</MenuItem>
                      <MenuItem value={3}>{t('calendar.settings.weekdays.wednesday')}</MenuItem>
                      <MenuItem value={4}>{t('calendar.settings.weekdays.thursday')}</MenuItem>
                      <MenuItem value={5}>{t('calendar.settings.weekdays.friday')}</MenuItem>
                      <MenuItem value={6}>{t('calendar.settings.weekdays.saturday')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('calendar.settings.startTime')}
                    type="time"
                    value={scheduleForm.start_time}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, start_time: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('calendar.settings.endTime')}
                    type="time"
                    value={scheduleForm.end_time}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, end_time: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t('calendar.settings.status')}</InputLabel>
                    <Select
                      value={scheduleForm.is_working_day ? '1' : '0'}
                      label={t('calendar.settings.status')}
                      onChange={(e) =>
                        setScheduleForm((prev) => ({
                          ...prev,
                          is_working_day: e.target.value === '1',
                        }))
                      }
                    >
                      <MenuItem value="1">{t('calendar.legend.workingDay')}</MenuItem>
                      <MenuItem value="0">{t('calendar.legend.offDay')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleScheduleSubmit}
                  disabled={scheduleSaving}
                >
                  {scheduleSaving ? t('common.saving') : t('calendar.settings.saveSchedule')}
                </Button>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('calendar.settings.currentWeeklyConfig')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))', gap: 1 }}>
                  {[0, 1, 2, 3, 4, 5, 6].map((weekday) => {
                    const cfg = getWeekdayConfig(weekday);
                    const dayName = t(`calendar.settings.weekdays.${['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][weekday]}`);
                    const isWorking = cfg ? cfg.is_working_day : true;
                    const range = cfg ? `${cfg.start_time} - ${cfg.end_time}` : '09:00 - 17:00';
                    return (
                      <Paper
                        key={weekday}
                        variant="outlined"
                        sx={{
                          p: 1.2,
                          cursor: 'pointer',
                          borderColor:
                            lastSavedWeekday === weekday
                              ? 'success.main'
                              : scheduleForm.weekday === weekday
                                ? 'primary.main'
                                : 'divider',
                          bgcolor:
                            lastSavedWeekday === weekday ? 'rgba(76, 175, 80, 0.15)' : 'transparent',
                        }}
                        onClick={() => setScheduleForm(prev => ({ ...prev, weekday }))}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{dayName}</Typography>
                        <Typography variant="caption" color={isWorking ? 'success.main' : 'error.main'}>
                          {isWorking ? t('calendar.legend.workingDay') : t('calendar.legend.offDay')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {range}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>{t('calendar.settings.customDateOverrides')}</Typography>
              <Typography variant="body2" sx={{ color: 'grey.600', mb: 2 }}>
                {t('calendar.settings.overrideDefaultSchedules')}
              </Typography>

              {overrideFeedback && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOverrideFeedback('')}>
                  {overrideFeedback}
                </Alert>
              )}
              {overrideError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOverrideError('')}>
                  {overrideError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('calendar.settings.overrideDate')}
                    type="date"
                    value={overrideForm.date}
                    onChange={(e) => handleOverrideDateChange(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('calendar.settings.startTime')}
                    type="time"
                    value={overrideForm.start_time}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({ ...prev, start_time: e.target.value }))
                    }
                    fullWidth
                    disabled={!overrideForm.is_working_day}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('calendar.settings.endTime')}
                    type="time"
                    value={overrideForm.end_time}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({ ...prev, end_time: e.target.value }))
                    }
                    fullWidth
                    disabled={!overrideForm.is_working_day}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>{t('calendar.settings.status')}</InputLabel>
                    <Select
                      value={overrideForm.is_working_day}
                      label={t('calendar.settings.status')}
                      onChange={(e) =>
                        setOverrideForm((prev) => ({
                          ...prev,
                          is_working_day: e.target.value === true || e.target.value === 'true',
                        }))
                      }
                    >
                      <MenuItem value={true}>{t('calendar.legend.workingDay')}</MenuItem>
                      <MenuItem value={false}>{t('calendar.legend.offDay')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {overrideForm.date && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  {getDefaultWeekdayHint()}
                </Typography>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleOverrideSubmit} disabled={overrideSaving}>
                  {overrideSaving ? t('common.saving') : t('calendar.settings.saveOverride')}
                </Button>
                {overrideForm.date &&
                  dateOverrides.some((o) => o.date === overrideForm.date) && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleOverrideDelete(overrideForm.date)}
                      disabled={overrideDeletingDate === overrideForm.date}
                    >
                      {overrideDeletingDate === overrideForm.date
                        ? t('common.saving')
                        : t('calendar.settings.removeOverride')}
                    </Button>
                  )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('calendar.settings.existingOverrides')}
                </Typography>
                {dateOverrides.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('calendar.settings.noOverrides')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {dateOverrides.map((override) => {
                      const isWorking = Boolean(override.is_working_day);
                      const range = isWorking
                        ? `${toTimeInputValue(override.start_time)} - ${toTimeInputValue(override.end_time)}`
                        : '—';
                      const isSelected = overrideForm.date === override.date;
                      return (
                        <Paper
                          key={override.id || override.date}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            cursor: 'pointer',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                          }}
                          onClick={() => applyOverrideToForm(override)}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatOverrideListDate(override.date)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={isWorking ? 'success.main' : 'error.main'}
                            >
                              {isWorking ? t('calendar.legend.workingDay') : t('calendar.legend.offDay')}
                              {isWorking ? ` · ${range}` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              {t('calendar.settings.clickToEdit')}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t('calendar.settings.removeOverride')}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOverrideDelete(override.date);
                            }}
                            disabled={overrideDeletingDate === override.date}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('calendar.settings.publicBookingLanguage')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.600', mb: 2 }}>
                {t('calendar.settings.publicBookingLanguageHint')}
              </Typography>

              {publicLanguageFeedback && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPublicLanguageFeedback('')}>
                  {publicLanguageFeedback}
                </Alert>
              )}
              {publicLanguageError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPublicLanguageError('')}>
                  {publicLanguageError}
                </Alert>
              )}

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t('calendar.settings.publicBookingLanguage')}</InputLabel>
                    <Select
                      value={publicCalendarLanguage}
                      label={t('calendar.settings.publicBookingLanguage')}
                      onChange={(e) => setPublicCalendarLanguage(e.target.value)}
                    >
                      <MenuItem value="en">{t('calendar.settings.languageEnglish')}</MenuItem>
                      <MenuItem value="es">{t('calendar.settings.languageSpanish')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    onClick={handleSavePublicLanguage}
                    disabled={publicLanguageSaving}
                  >
                    {publicLanguageSaving ? t('common.saving') : t('calendar.settings.savePublicLanguage')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {scheduleFeedback && (
              <Typography variant="body2" color="success.main" fontWeight={600}>
                {scheduleFeedback}
              </Typography>
            )}
            {scheduleError && (
              <Typography variant="body2" color="error.main" fontWeight={600}>
                {scheduleError}
              </Typography>
            )}
          </Box>
          <Button onClick={() => setSettingsModalOpen(false)}>{t('calendar.form.close')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={scheduleSnackOpen}
        autoHideDuration={7000}
        onClose={() => setScheduleSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setScheduleSnackOpen(false)}
          severity={scheduleError ? 'error' : 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {scheduleError || scheduleFeedback}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(appointmentToCancel)}
        onClose={() => !cancellingAppointment && setAppointmentToCancel(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('calendar.form.cancelConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {appointmentToCancel
              ? t('calendar.form.cancelConfirmMessage', {
                  patient: appointmentDisplayName(appointmentToCancel),
                  date: selectedDate || '',
                  time: `${formatAppointmentTime(appointmentToCancel.start_time)} – ${formatAppointmentTime(appointmentToCancel.end_time)}`,
                })
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAppointmentToCancel(null)} disabled={cancellingAppointment}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmAppointmentCancel}
            disabled={cancellingAppointment}
          >
            {cancellingAppointment ? t('common.saving') : t('calendar.form.cancelConfirmAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Dialog */}
      <AppointmentDialog
        open={appointmentDialogOpen}
        onClose={() => {
          setAppointmentDialogOpen(false);
          // If opened from client quick-action, we don't want to keep a stale selected date.
          if (initialClientId) setSelectedDate(null);
        }}
        selectedSlot={selectedSlot}
        selectedDate={selectedDate}
        initialClientId={initialClientId}
        onAppointmentCreated={async () => {
          await loadMonthSchedule();
          if (dayModalOpen && selectedDate) {
            await openDayModal(selectedDate);
          } else {
            closeDayModal();
          }
        }}
      />
    </Box>
  );
};

export default Calendar;
